import { useCallback, useEffect, useMemo, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {

  addMonths,

  eachDayOfInterval,

  endOfMonth,

  endOfWeek,

  format,

  isSameDay,

  isSameMonth,

  parseISO,

  setHours,

  setMinutes,

  startOfMonth,

  startOfWeek,

} from 'date-fns';

import { socialApi } from '@/api';

import { PageHeader } from '@/components/layout/PageHeader';

import { Button } from '@/components/ui/Button';

import { Tooltip } from '@/components/ui/Tooltip';

import type { SocialCalendarEntry } from '@/types/social';

import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DRAG_TYPE = 'social/calendar-post';



function monthBounds(date: Date) {

  const from = startOfMonth(date);

  const to = endOfMonth(date);

  return { from: format(from, "yyyy-MM-dd'T'00:00:00"), to: format(to, "yyyy-MM-dd'T'23:59:59") };

}



function defaultTimeFromEntry(entry: SocialCalendarEntry): { hours: number; minutes: number } {

  if (entry.scheduledAt) {

    const d = parseISO(entry.scheduledAt);

    return { hours: d.getHours(), minutes: d.getMinutes() };

  }

  return { hours: 10, minutes: 0 };

}



export function SocialCalendarPage() {

  const [entries, setEntries] = useState<SocialCalendarEntry[]>([]);

  const [loading, setLoading] = useState(true);

  const [viewDate, setViewDate] = useState(() => new Date());

  const [draggingId, setDraggingId] = useState<number | null>(null);

  const [rescheduling, setRescheduling] = useState(false);



  const load = useCallback(async () => {

    setLoading(true);

    const { from, to } = monthBounds(viewDate);

    try {

      const page = await socialApi.listCalendar(from, to);

      setEntries(page.content ?? []);

    } catch (err) {

      showApiError(err, 'Calendar unavailable on current plan');

    } finally {

      setLoading(false);

    }

  }, [viewDate]);



  useEffect(() => {

    load();

  }, [load]);



  const calendarDays = useMemo(() => {

    const monthStart = startOfMonth(viewDate);

    const monthEnd = endOfMonth(viewDate);

    return eachDayOfInterval({

      start: startOfWeek(monthStart),

      end: endOfWeek(monthEnd),

    });

  }, [viewDate]);



  const entriesByDay = useMemo(() => {

    const map = new Map<string, SocialCalendarEntry[]>();

    for (const entry of entries) {

      if (!entry.scheduledAt) continue;

      const key = format(parseISO(entry.scheduledAt), 'yyyy-MM-dd');

      const list = map.get(key) ?? [];

      list.push(entry);

      map.set(key, list);

    }

    return map;

  }, [entries]);



  const rescheduleToDay = async (postId: number, targetDay: Date) => {

    const entry = entries.find((e) => e.postId === postId);

    if (!entry) return;

    const { hours, minutes } = defaultTimeFromEntry(entry);

    const scheduledAt = setMinutes(setHours(targetDay, hours), minutes).toISOString();

    setRescheduling(true);

    try {

      await socialApi.reschedulePost(postId, scheduledAt);

      notify.success('Post rescheduled');

      load();

    } catch (err) {

      showApiError(err, 'Reschedule failed');

    } finally {

      setRescheduling(false);

      setDraggingId(null);

    }

  };



  const onDragStart = (e: React.DragEvent, postId: number) => {

    e.dataTransfer.setData(DRAG_TYPE, String(postId));

    e.dataTransfer.effectAllowed = 'move';

    setDraggingId(postId);

  };



  const onDragOver = (e: React.DragEvent) => {

    e.preventDefault();

    e.dataTransfer.dropEffect = 'move';

  };



  const onDrop = (e: React.DragEvent, day: Date) => {

    e.preventDefault();

    const raw = e.dataTransfer.getData(DRAG_TYPE);

    if (!raw) return;

    rescheduleToDay(Number(raw), day);

  };



  return (

    <div>

      <PageHeader

        title="Content Calendar"

        description="Drag posts between days to reschedule — auto-publish runs every 30 seconds"

        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Social', to: '/social' }, { label: 'Calendar' }]}

        actions={

          <div className="flex items-center gap-2">

            <Button variant="secondary" onClick={() => setViewDate((d) => addMonths(d, -1))}>

              <ChevronLeftIcon fontSize="small" />

            </Button>

            <span className="min-w-[10rem] text-center text-sm font-medium">

              {format(viewDate, 'MMMM yyyy')}

            </span>

            <Button variant="secondary" onClick={() => setViewDate((d) => addMonths(d, 1))}>

              <ChevronRightIcon fontSize="small" />

            </Button>

          </div>

        }

      />



      {rescheduling && <p className="mb-2 text-sm text-slate-500">Saving new schedule…</p>}



      <div className="card overflow-hidden">

        <div className="grid grid-cols-7 border-b bg-slate-50 text-xs font-semibold uppercase tracking-wide dark:bg-slate-800/50">

          {WEEKDAYS.map((d) => (

            <div key={d} className="px-2 py-2 text-center text-slate-500">{d}</div>

          ))}

        </div>

        <div className="grid grid-cols-7">

          {calendarDays.map((day) => {

            const key = format(day, 'yyyy-MM-dd');

            const dayEntries = entriesByDay.get(key) ?? [];

            const inMonth = isSameMonth(day, viewDate);

            const isToday = isSameDay(day, new Date());

            return (

              <div

                key={key}

                className={`min-h-[7rem] border-b border-r border-slate-100 p-1 dark:border-slate-800 ${

                  inMonth ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/80 dark:bg-slate-950/50'

                } ${draggingId != null ? 'ring-inset hover:ring-2 hover:ring-primary-300' : ''}`}

                onDragOver={onDragOver}

                onDrop={(e) => onDrop(e, day)}

              >

                <div className={`mb-1 text-xs font-medium ${isToday ? 'text-primary-600' : inMonth ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`}>

                  {format(day, 'd')}

                </div>

                <div className="space-y-1">

                  {loading && inMonth && dayEntries.length === 0 ? null : dayEntries.map((entry) => (
                    <Tooltip key={entry.postId} content={entry.content}>
                      <div
                        draggable={entry.postStatus === 'SCHEDULED' || entry.postStatus === 'DRAFT' || entry.postStatus === 'APPROVED'}
                        onDragStart={(e) => onDragStart(e, entry.postId)}
                        onDragEnd={() => setDraggingId(null)}
                        className={`cursor-grab rounded px-1.5 py-1 text-xs active:cursor-grabbing ${
                          draggingId === entry.postId
                            ? 'bg-primary-100 opacity-60 dark:bg-primary-900/40'
                            : 'bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/40'
                        }`}
                      >

                      <p className="truncate font-medium">{entry.title || entry.content.slice(0, 40)}</p>

                      <p className="text-[10px] text-slate-500">

                        {entry.scheduledAt ? format(parseISO(entry.scheduledAt), 'HH:mm') : '—'} · {entry.postStatus}

                      </p>

                    </div>
                    </Tooltip>
                  ))}

                </div>

              </div>

            );

          })}

        </div>

      </div>



      {!loading && entries.length === 0 && (

        <p className="mt-4 text-sm text-slate-500">

          No scheduled posts this month. Create one from Publishing with a future date and time.

        </p>

      )}

    </div>

  );

}


